import type { SiteLang } from "@/hooks/useLanguage";

export type GlossaryCategoryKey =
  | "regulatory"
  | "quality"
  | "clinical"
  | "pharmacovigilance"
  | "devices"
  | "cmc";

export interface LocalizedText {
  en: string;
  es: string;
  "zh-cn": string;
}

export interface GlossaryTerm {
  /** Display term (acronyms stay in English across all locales) */
  term: string;
  /** Stable slug, used for anchors today and term pages later */
  slug: string;
  /** What the acronym stands for, when relevant */
  expansion?: LocalizedText;
  category: GlossaryCategoryKey;
  /** Short, citable definition (40-60 words) */
  definition: LocalizedText;
  /** Extra search terms (synonyms, spelled-out forms) */
  synonyms?: string[];
  /** Surfaced in the "most looked up" block */
  popular?: boolean;
  /** Related QbD Group page, given as an /en path and localized at render time */
  relatedHref?: string;
  relatedLabel?: LocalizedText;
}

export const GLOSSARY_CATEGORIES: { key: GlossaryCategoryKey; label: LocalizedText }[] = [
  {
    key: "regulatory",
    label: { en: "Regulatory Affairs", es: "Regulatory Affairs", "zh-cn": "法规事务" },
  },
  {
    key: "quality",
    label: { en: "Quality & GMP", es: "Calidad y GMP", "zh-cn": "质量与 GMP" },
  },
  {
    key: "clinical",
    label: { en: "Clinical", es: "Clínico", "zh-cn": "临床" },
  },
  {
    key: "pharmacovigilance",
    label: { en: "Pharmacovigilance", es: "Farmacovigilancia", "zh-cn": "药物警戒" },
  },
  {
    key: "devices",
    label: { en: "Devices & Software", es: "Medical Devices y Software", "zh-cn": "器械与软件" },
  },
  {
    key: "cmc",
    label: { en: "CMC & Manufacturing", es: "CMC y Fabricación", "zh-cn": "CMC 与生产" },
  },
];

export const GLOSSARY_TERMS: GlossaryTerm[] = [
  // ── Regulatory Affairs ───────────────────────────────────────────────
  {
    term: "EU MDR",
    slug: "eu-mdr",
    category: "regulatory",
    popular: true,
    synonyms: ["Medical Device Regulation", "2017/745"],
    expansion: {
      en: "Regulation (EU) 2017/745 on medical devices",
      es: "Reglamento (UE) 2017/745 sobre Medical Devices",
      "zh-cn": "关于医疗器械的法规 (EU) 2017/745",
    },
    definition: {
      en: "The European regulation governing the design, manufacture, and placing on the market of medical devices. It replaced the Medical Device Directive and tightened clinical evidence, technical documentation, traceability, and post-market surveillance requirements for all device classes.",
      es: "El reglamento europeo que regula el diseño, la fabricación y la comercialización de Medical Devices. Sustituyó a la Directiva de Productos Sanitarios y endureció los requisitos de evidencia clínica, documentación técnica, trazabilidad y vigilancia poscomercialización para todas las clases.",
      "zh-cn": "规范医疗器械设计、生产和上市的欧盟法规。它取代了医疗器械指令，并对所有类别器械的临床证据、技术文档、可追溯性和上市后监督提出了更严格的要求。",
    },
    relatedHref: "/en/industries/medical-devices/gateway-to-europe",
    relatedLabel: {
      en: "MedTech Gateway to Europe",
      es: "Acceso al mercado europeo para MedTech",
      "zh-cn": "MedTech 欧洲市场准入",
    },
  },
  {
    term: "IVDR",
    slug: "ivdr",
    category: "regulatory",
    popular: true,
    synonyms: ["In Vitro Diagnostic Regulation", "2017/746"],
    expansion: {
      en: "Regulation (EU) 2017/746 on in vitro diagnostic medical devices",
      es: "Reglamento (UE) 2017/746 sobre productos sanitarios para diagnóstico in vitro",
      "zh-cn": "关于体外诊断医疗器械的法规 (EU) 2017/746",
    },
    definition: {
      en: "The European regulation for in vitro diagnostic medical devices. It introduced a risk-based classification (A to D), moved most IVDs under notified body oversight, and requires performance evaluation covering scientific validity, analytical performance, and clinical performance.",
      es: "El reglamento europeo para productos sanitarios de diagnóstico in vitro. Introdujo una clasificación basada en riesgo (A a D), sometió la mayoría de los IVD a la supervisión de un organismo notificado y exige una evaluación del rendimiento que cubra validez científica, rendimiento analítico y rendimiento clínico.",
      "zh-cn": "针对体外诊断医疗器械的欧盟法规。它引入基于风险的分类（A 至 D），将大多数 IVD 纳入公告机构监管，并要求开展涵盖科学有效性、分析性能和临床性能的性能评估。",
    },
    relatedHref: "/en/industries/medical-devices",
    relatedLabel: {
      en: "Medical Devices & IVD",
      es: "Medical Devices e IVD",
      "zh-cn": "医疗器械与 IVD",
    },
  },
  {
    term: "Notified Body",
    slug: "notified-body",
    category: "regulatory",
    popular: true,
    definition: {
      en: "An independent organisation designated by an EU member state to assess whether medical devices and IVDs meet regulatory requirements before CE marking. Its conformity assessment covers the quality management system, technical documentation, and ongoing surveillance audits.",
      es: "Organización independiente designada por un Estado miembro de la UE para evaluar si los Medical Devices y los IVD cumplen los requisitos regulatorios antes del marcado CE. Su evaluación de la conformidad abarca el sistema de gestión de calidad, la documentación técnica y las auditorías de vigilancia continuas.",
      "zh-cn": "由欧盟成员国指定的独立机构，在 CE 标志前评估医疗器械和 IVD 是否符合法规要求。其符合性评估涵盖质量管理体系、技术文档以及持续的监督审核。",
    },
  },
  {
    term: "CE Marking",
    slug: "ce-marking",
    category: "regulatory",
    definition: {
      en: "The manufacturer's declaration that a product complies with the applicable EU legislation and may be placed on the European market. For medical devices and IVDs it follows a conformity assessment, usually involving a notified body outside the lowest risk classes.",
      es: "Declaración del fabricante de que un producto cumple la legislación aplicable de la UE y puede comercializarse en el mercado europeo. Para Medical Devices e IVD se obtiene tras una evaluación de la conformidad, normalmente con un organismo notificado salvo en las clases de menor riesgo.",
      "zh-cn": "制造商声明产品符合适用的欧盟法规并可在欧洲市场投放。对于医疗器械和 IVD，需先完成符合性评估，除最低风险类别外通常需要公告机构参与。",
    },
  },
  {
    term: "MAA",
    slug: "maa",
    category: "regulatory",
    expansion: {
      en: "Marketing Authorisation Application",
      es: "Solicitud de autorización de comercialización",
      "zh-cn": "上市许可申请",
    },
    definition: {
      en: "The dossier submitted to EMA or a national authority requesting permission to market a medicinal product in Europe. It compiles quality, non-clinical, and clinical data in the Common Technical Document format, together with labelling and risk management documentation.",
      es: "Expediente presentado a la EMA o a una autoridad nacional para solicitar la comercialización de un medicamento en Europa. Recopila datos de calidad, no clínicos y clínicos en formato Common Technical Document, junto con el etiquetado y la documentación de gestión de riesgos.",
      "zh-cn": "向 EMA 或国家主管机构提交的申请文件，请求在欧洲上市某一药品。它以通用技术文件格式汇总质量、非临床和临床数据，并附标签及风险管理文件。",
    },
  },
  {
    term: "NDA",
    slug: "nda",
    category: "regulatory",
    expansion: {
      en: "New Drug Application",
      es: "Solicitud de nuevo medicamento (New Drug Application)",
      "zh-cn": "新药申请",
    },
    definition: {
      en: "The application through which a sponsor asks the FDA to approve a new medicinal product for the United States market. It presents the full development package: chemistry and manufacturing, non-clinical safety, clinical efficacy and safety, and proposed labelling.",
      es: "Solicitud mediante la cual un promotor pide a la FDA la aprobación de un nuevo medicamento para el mercado estadounidense. Presenta el paquete completo de desarrollo: química y fabricación, seguridad no clínica, eficacia y seguridad clínicas, y el etiquetado propuesto.",
      "zh-cn": "申办方请求 FDA 批准新药在美国上市的申请。它提交完整的开发资料：化学与生产、非临床安全性、临床有效性与安全性以及拟定标签。",
    },
  },
  {
    term: "ANDA",
    slug: "anda",
    category: "regulatory",
    expansion: {
      en: "Abbreviated New Drug Application",
      es: "Solicitud abreviada de nuevo medicamento",
      "zh-cn": "简略新药申请",
    },
    definition: {
      en: "The FDA pathway for generic medicines. Instead of repeating clinical efficacy trials, the applicant demonstrates that the generic is pharmaceutically equivalent and bioequivalent to an approved reference product, alongside a full quality and manufacturing dossier.",
      es: "Vía de la FDA para medicamentos genéricos. En lugar de repetir los ensayos de eficacia clínica, el solicitante demuestra que el genérico es equivalente farmacéutico y bioequivalente a un producto de referencia aprobado, junto con un expediente completo de calidad y fabricación.",
      "zh-cn": "FDA 针对仿制药的申报途径。申请人无需重复临床有效性试验，而是证明仿制药与已批准参比制剂具有药学等效性和生物等效性，并提交完整的质量与生产资料。",
    },
  },
  {
    term: "Technical Documentation",
    slug: "technical-documentation",
    category: "regulatory",
    synonyms: ["technical file", "design dossier"],
    definition: {
      en: "The structured evidence file a device or IVD manufacturer maintains to demonstrate conformity, as set out in MDR/IVDR Annexes II and III. It covers device description, design and manufacturing information, risk management, verification and validation, clinical evidence, and post-market data.",
      es: "Expediente estructurado de evidencia que mantiene el fabricante de un Medical Device o IVD para demostrar la conformidad, según los Anexos II y III del MDR/IVDR. Incluye descripción del producto, diseño y fabricación, gestión de riesgos, verificación y validación, evidencia clínica y datos poscomercialización.",
      "zh-cn": "器械或 IVD 制造商为证明符合性而维护的结构化证据文件，依据 MDR/IVDR 附录 II 和 III。内容涵盖产品描述、设计与制造信息、风险管理、验证与确认、临床证据以及上市后数据。",
    },
  },
  {
    term: "EUDAMED",
    slug: "eudamed",
    category: "regulatory",
    expansion: {
      en: "European Database on Medical Devices",
      es: "Base de datos europea sobre productos sanitarios",
      "zh-cn": "欧洲医疗器械数据库",
    },
    definition: {
      en: "The European Commission database supporting MDR and IVDR. Its modules cover actor registration, UDI and device registration, notified bodies and certificates, clinical investigations and performance studies, vigilance, and market surveillance.",
      es: "Base de datos de la Comisión Europea que da soporte al MDR y al IVDR. Sus módulos cubren el registro de agentes, el UDI y el registro de productos, organismos notificados y certificados, investigaciones clínicas y estudios de rendimiento, vigilancia y control del mercado.",
      "zh-cn": "支持 MDR 与 IVDR 的欧盟委员会数据库。其模块涵盖主体注册、UDI 与器械注册、公告机构与证书、临床研究与性能研究、警戒以及市场监督。",
    },
  },
  {
    term: "UDI",
    slug: "udi",
    category: "regulatory",
    expansion: {
      en: "Unique Device Identification",
      es: "Identificación única del producto",
      "zh-cn": "唯一器械标识",
    },
    definition: {
      en: "A globally unique code carried on device labels and packaging so that a product can be identified and traced through distribution and use. It combines a device identifier for the model with a production identifier for batch, serial number, or expiry.",
      es: "Código único a escala mundial que figura en las etiquetas y el envase del producto para poder identificarlo y trazarlo a lo largo de la distribución y el uso. Combina un identificador de producto para el modelo con un identificador de producción para lote, número de serie o caducidad.",
      "zh-cn": "标注在器械标签和包装上的全球唯一编码，用于在流通和使用过程中识别与追溯产品。它由标识型号的器械标识符与标识批号、序列号或有效期的生产标识符组成。",
    },
  },
  {
    term: "Scientific Advice",
    slug: "scientific-advice",
    category: "regulatory",
    definition: {
      en: "A formal consultation in which a developer asks a regulator, such as EMA or a national agency, for guidance on its development plan. Advice typically covers quality, non-clinical, and clinical strategy and reduces the risk of objections later in the procedure.",
      es: "Consulta formal en la que un desarrollador solicita a un regulador, como la EMA o una agencia nacional, orientación sobre su plan de desarrollo. Suele abarcar la estrategia de calidad, no clínica y clínica, y reduce el riesgo de objeciones en fases posteriores.",
      "zh-cn": "开发方向 EMA 或国家药监机构等监管方就其开发计划征求指导的正式咨询。通常涵盖质量、非临床与临床策略，可降低后续程序中被提出异议的风险。",
    },
  },
  {
    term: "Lifecycle Management",
    slug: "lifecycle-management",
    category: "regulatory",
    synonyms: ["LCM", "variations"],
    definition: {
      en: "The ongoing regulatory maintenance of an authorised product: variations, renewals, labelling updates, new indications, site or supplier changes, and responses to authority requests, all managed so the authorisation stays valid and current.",
      es: "Mantenimiento regulatorio continuo de un producto autorizado: variaciones, renovaciones, actualizaciones de etiquetado, nuevas indicaciones, cambios de planta o proveedor y respuestas a las autoridades, gestionados para que la autorización siga siendo válida y esté actualizada.",
      "zh-cn": "对已获批产品的持续法规维护：变更、再注册、标签更新、新增适应症、场地或供应商变更以及对监管机构问询的答复，以确保许可持续有效并保持最新。",
    },
  },

  // ── Quality & GMP ────────────────────────────────────────────────────
  {
    term: "GMP",
    slug: "gmp",
    category: "quality",
    popular: true,
    expansion: {
      en: "Good Manufacturing Practice",
      es: "Buenas Prácticas de Fabricación",
      "zh-cn": "药品生产质量管理规范",
    },
    definition: {
      en: "The set of rules ensuring medicinal products are consistently produced and controlled to the quality standards required by their intended use and marketing authorisation. In Europe it is set out in EudraLex Volume 4; in the US in 21 CFR Parts 210 and 211.",
      es: "Conjunto de normas que garantiza que los medicamentos se fabriquen y controlen de forma consistente según los estándares de calidad exigidos por su uso previsto y su autorización. En Europa se recoge en EudraLex Volumen 4; en EE. UU., en 21 CFR Partes 210 y 211.",
      "zh-cn": "确保药品按照其预期用途和上市许可所要求的质量标准持续生产和控制的规则体系。欧洲规定见 EudraLex 第 4 卷，美国规定见 21 CFR 第 210 和 211 部分。",
    },
    relatedHref: "/en/services/quality-assurance",
    relatedLabel: {
      en: "Quality Assurance services",
      es: "Servicios de Quality Assurance",
      "zh-cn": "质量保证服务",
    },
  },
  {
    term: "GDP",
    slug: "gdp",
    category: "quality",
    expansion: {
      en: "Good Distribution Practice",
      es: "Buenas Prácticas de Distribución",
      "zh-cn": "良好分销规范",
    },
    definition: {
      en: "The quality standard for the storage, transport, and distribution of medicinal products. It covers wholesale licensing, qualified personnel, temperature control and mapping, supplier and customer qualification, and the handling of returns and falsified medicines.",
      es: "Estándar de calidad para el almacenamiento, transporte y distribución de medicamentos. Cubre la licencia mayorista, el personal cualificado, el control y mapeo de temperatura, la cualificación de proveedores y clientes y la gestión de devoluciones y medicamentos falsificados.",
      "zh-cn": "药品储存、运输和分销的质量标准。涵盖批发许可、合格人员、温度控制与测绘、供应商与客户资质审核，以及退货和假药的处理。",
    },
  },
  {
    term: "GLP",
    slug: "glp",
    category: "quality",
    expansion: {
      en: "Good Laboratory Practice",
      es: "Buenas Prácticas de Laboratorio",
      "zh-cn": "良好实验室规范",
    },
    definition: {
      en: "The quality system governing how non-clinical safety studies are planned, performed, recorded, reported, and archived, so that regulators can rely on the data submitted in a marketing application.",
      es: "Sistema de calidad que rige cómo se planifican, realizan, registran, comunican y archivan los estudios de seguridad no clínicos, de modo que los reguladores puedan confiar en los datos presentados en una solicitud de comercialización.",
      "zh-cn": "规范非临床安全性研究如何计划、执行、记录、报告和归档的质量体系，使监管机构能够信赖上市申请中提交的数据。",
    },
  },
  {
    term: "ISO 13485",
    slug: "iso-13485",
    category: "quality",
    popular: true,
    definition: {
      en: "The international standard for quality management systems specific to medical devices. Certification is the practical route to demonstrating the QMS requirements of MDR Article 10 and IVDR Article 10, and is expected by most notified bodies and customers.",
      es: "Norma internacional de sistemas de gestión de calidad específica para Medical Devices. La certificación es la vía práctica para demostrar los requisitos de QMS del Artículo 10 del MDR y del IVDR, y la esperan la mayoría de organismos notificados y clientes.",
      "zh-cn": "面向医疗器械的质量管理体系国际标准。取得认证是证明 MDR 第 10 条和 IVDR 第 10 条质量管理体系要求的实用途径，也是多数公告机构和客户的期望。",
    },
  },
  {
    term: "QMS",
    slug: "qms",
    category: "quality",
    expansion: {
      en: "Quality Management System",
      es: "Sistema de Gestión de Calidad",
      "zh-cn": "质量管理体系",
    },
    definition: {
      en: "The documented set of processes, responsibilities, and records through which an organisation plans, controls, and improves product quality and regulatory compliance, from design and supplier control through production, release, complaints, and CAPA.",
      es: "Conjunto documentado de procesos, responsabilidades y registros con el que una organización planifica, controla y mejora la calidad del producto y el cumplimiento regulatorio, desde el diseño y el control de proveedores hasta la producción, la liberación, las reclamaciones y las CAPA.",
      "zh-cn": "组织用于策划、控制和改进产品质量与法规符合性的成文流程、职责和记录体系，涵盖设计、供应商控制、生产、放行、投诉与 CAPA。",
    },
  },
  {
    term: "CAPA",
    slug: "capa",
    category: "quality",
    expansion: {
      en: "Corrective And Preventive Action",
      es: "Acción Correctiva y Preventiva",
      "zh-cn": "纠正与预防措施",
    },
    definition: {
      en: "The quality process for investigating a problem, correcting it, addressing its root cause so it does not recur, and preventing similar issues elsewhere. Effectiveness checks close the loop, and CAPA records are a standard focus of inspections and audits.",
      es: "Proceso de calidad para investigar un problema, corregirlo, abordar su causa raíz para que no se repita y prevenir incidencias similares en otros ámbitos. Las verificaciones de eficacia cierran el ciclo, y los registros CAPA son un foco habitual de inspecciones y auditorías.",
      "zh-cn": "用于调查问题、予以纠正、消除根本原因以防再次发生并防止其他环节出现类似问题的质量流程。有效性验证使闭环完成，CAPA 记录是检查与审核的常规关注点。",
    },
  },
  {
    term: "Deviation",
    slug: "deviation",
    category: "quality",
    definition: {
      en: "A departure from an approved procedure, specification, or standard during a GMP activity. Each deviation is documented, classified by impact, investigated for root cause, and assessed for product quality impact before batch disposition.",
      es: "Desviación respecto a un procedimiento, especificación o estándar aprobado durante una actividad GMP. Cada desviación se documenta, se clasifica por impacto, se investiga su causa raíz y se evalúa su repercusión en la calidad del producto antes de decidir sobre el lote.",
      "zh-cn": "GMP 活动中偏离已批准程序、质量标准或规范的情况。每项偏差都需记录、按影响分级、开展根本原因调查，并在批处置前评估对产品质量的影响。",
    },
  },
  {
    term: "Change Control",
    slug: "change-control",
    category: "quality",
    definition: {
      en: "The formal system for proposing, assessing, approving, implementing, and documenting changes to facilities, equipment, materials, processes, or documentation, including the regulatory impact assessment that determines whether a variation must be filed.",
      es: "Sistema formal para proponer, evaluar, aprobar, implementar y documentar cambios en instalaciones, equipos, materiales, procesos o documentación, incluida la evaluación del impacto regulatorio que determina si debe presentarse una variación.",
      "zh-cn": "对厂房、设备、物料、工艺或文件的变更进行提出、评估、批准、实施和记录的正式体系，其中包括判断是否需要提交变更申报的法规影响评估。",
    },
  },
  {
    term: "CQV",
    slug: "cqv",
    category: "quality",
    expansion: {
      en: "Commissioning, Qualification and Validation",
      es: "Puesta en marcha, cualificación y validación",
      "zh-cn": "调试、确认与验证",
    },
    definition: {
      en: "The staged programme that brings facilities, utilities, equipment, and processes into GMP service: commissioning of the installation, qualification against design and operating requirements, and validation that the process reliably delivers the required quality.",
      es: "Programa por etapas que pone en servicio GMP instalaciones, utilidades, equipos y procesos: puesta en marcha de la instalación, cualificación frente a los requisitos de diseño y operación, y validación de que el proceso ofrece de forma fiable la calidad requerida.",
      "zh-cn": "使厂房、公用系统、设备和工艺投入 GMP 使用的分阶段方案：装置调试、依据设计与运行要求进行确认，并验证工艺能够可靠地达到所需质量。",
    },
  },
  {
    term: "Qualified Person",
    slug: "qualified-person",
    category: "quality",
    synonyms: ["QP", "batch release"],
    definition: {
      en: "The named individual legally responsible under EU law for certifying that each batch of a medicinal product was manufactured and tested in line with GMP and the marketing authorisation before it is released to the market.",
      es: "Persona designada legalmente responsable, según la legislación de la UE, de certificar que cada lote de un medicamento se ha fabricado y analizado conforme a las GMP y a la autorización de comercialización antes de su liberación al mercado.",
      "zh-cn": "依据欧盟法律承担法定责任的指定人员，在药品每一批次放行上市前，证明其生产与检验符合 GMP 和上市许可要求。",
    },
  },
  {
    term: "MIA",
    slug: "mia",
    category: "quality",
    expansion: {
      en: "Manufacturing and Importation Authorisation",
      es: "Autorización de fabricación e importación",
      "zh-cn": "生产与进口许可",
    },
    definition: {
      en: "The licence a national competent authority grants for manufacturing, importing, or batch-releasing medicinal products in the EU. It names the authorised sites, activities, dosage forms, and the Qualified Persons attached to the licence.",
      es: "Licencia que concede una autoridad nacional competente para fabricar, importar o liberar lotes de medicamentos en la UE. Indica las plantas autorizadas, las actividades, las formas farmacéuticas y las Qualified Persons vinculadas a la licencia.",
      "zh-cn": "由国家主管机构签发的在欧盟生产、进口或放行药品的许可证。其中列明获授权的场地、活动范围、剂型以及与许可关联的质量受权人。",
    },
  },
  {
    term: "Inspection Readiness",
    slug: "inspection-readiness",
    category: "quality",
    definition: {
      en: "The state of being able to host a regulatory inspection at short notice: current documentation, traceable records, trained staff, closed CAPAs, and a rehearsed front-room and back-room process for handling requests during the inspection.",
      es: "Situación de poder recibir una inspección regulatoria con poca antelación: documentación actualizada, registros trazables, personal formado, CAPA cerradas y un proceso ensayado de front room y back room para gestionar las peticiones durante la inspección.",
      "zh-cn": "能够在短时间通知下接受监管检查的状态：文件现行有效、记录可追溯、人员经过培训、CAPA 已关闭，并具备演练过的前台与后台流程以应对检查期间的资料索取。",
    },
  },

  // ── Clinical ─────────────────────────────────────────────────────────
  {
    term: "GCP",
    slug: "gcp",
    category: "clinical",
    expansion: {
      en: "Good Clinical Practice",
      es: "Buenas Prácticas Clínicas",
      "zh-cn": "药物临床试验质量管理规范",
    },
    definition: {
      en: "The international ethical and scientific quality standard for designing, conducting, recording, and reporting clinical trials involving human subjects, set out in ICH E6. Compliance protects participant rights and safety and makes trial data credible to regulators.",
      es: "Estándar internacional ético y científico de calidad para diseñar, realizar, registrar y comunicar ensayos clínicos con seres humanos, recogido en la ICH E6. Su cumplimiento protege los derechos y la seguridad de los participantes y hace que los datos sean creíbles para los reguladores.",
      "zh-cn": "ICH E6 规定的国际伦理与科学质量标准，用于人体临床试验的设计、实施、记录和报告。遵守该规范可保护受试者权益与安全，并使试验数据获得监管机构认可。",
    },
    relatedHref: "/en/services/clinical-solutions",
    relatedLabel: {
      en: "Clinical Solutions",
      es: "Clinical Solutions",
      "zh-cn": "临床解决方案",
    },
  },
  {
    term: "CTR 536/2014",
    slug: "ctr-536-2014",
    category: "clinical",
    expansion: {
      en: "EU Clinical Trials Regulation",
      es: "Reglamento europeo de ensayos clínicos",
      "zh-cn": "欧盟临床试验法规",
    },
    definition: {
      en: "The regulation harmonising clinical trial authorisation and supervision across the EU. It introduced a single submission through CTIS, coordinated assessment between member states, defined assessment timelines, and public transparency of trial information.",
      es: "Reglamento que armoniza la autorización y supervisión de ensayos clínicos en la UE. Introdujo una presentación única a través de CTIS, la evaluación coordinada entre Estados miembros, plazos definidos de evaluación y la transparencia pública de la información del ensayo.",
      "zh-cn": "统一欧盟临床试验审批与监管的法规。它引入通过 CTIS 的单一提交、成员国之间的协调评估、明确的评估时限以及试验信息的公开透明。",
    },
  },
  {
    term: "CTIS",
    slug: "ctis",
    category: "clinical",
    expansion: {
      en: "Clinical Trials Information System",
      es: "Sistema de información de ensayos clínicos",
      "zh-cn": "临床试验信息系统",
    },
    definition: {
      en: "The single EU portal and database for clinical trial applications under CTR 536/2014. Sponsors submit one dossier for all concerned member states and manage the full lifecycle there: authorisation, substantial modifications, safety reporting, and results.",
      es: "Portal y base de datos únicos de la UE para las solicitudes de ensayos clínicos bajo el CTR 536/2014. Los promotores presentan un único expediente para todos los Estados miembros implicados y gestionan allí todo el ciclo: autorización, modificaciones sustanciales, notificación de seguridad y resultados.",
      "zh-cn": "依据 CTR 536/2014 的欧盟统一临床试验申请门户与数据库。申办方为所有相关成员国提交同一份资料，并在此管理全生命周期：审批、重大修改、安全性报告与结果。",
    },
  },
  {
    term: "CTA",
    slug: "cta",
    category: "clinical",
    expansion: {
      en: "Clinical Trial Application",
      es: "Solicitud de ensayo clínico",
      "zh-cn": "临床试验申请",
    },
    definition: {
      en: "The application seeking authorisation to run a clinical trial in a given territory. It bundles the protocol, investigator's brochure, IMP dossier, informed consent documents, and site and investigator information for regulatory and ethics review.",
      es: "Solicitud para obtener la autorización de realizar un ensayo clínico en un territorio determinado. Reúne el protocolo, el manual del investigador, el expediente del medicamento en investigación, los documentos de consentimiento informado y la información de centros e investigadores para su revisión regulatoria y ética.",
      "zh-cn": "在特定地区开展临床试验的审批申请。内容包括方案、研究者手册、试验用药品资料、知情同意文件以及中心与研究者信息，供监管与伦理审查。",
    },
  },
  {
    term: "CER",
    slug: "cer",
    category: "clinical",
    popular: true,
    expansion: {
      en: "Clinical Evaluation Report",
      es: "Informe de evaluación clínica",
      "zh-cn": "临床评价报告",
    },
    definition: {
      en: "The MDR deliverable that documents the systematic appraisal of clinical data for a medical device. It weighs literature, clinical investigations, and post-market data against the intended purpose to confirm safety, performance, and an acceptable benefit-risk profile.",
      es: "Entregable del MDR que documenta la evaluación sistemática de los datos clínicos de un Medical Device. Contrasta bibliografía, investigaciones clínicas y datos poscomercialización con la finalidad prevista para confirmar la seguridad, el rendimiento y un perfil beneficio-riesgo aceptable.",
      "zh-cn": "MDR 要求的交付文件，记录对医疗器械临床数据的系统评价。它将文献、临床研究和上市后数据与预期用途相对照，以确认安全性、性能及可接受的获益风险比。",
    },
  },
  {
    term: "PMCF",
    slug: "pmcf",
    category: "clinical",
    expansion: {
      en: "Post-Market Clinical Follow-up",
      es: "Seguimiento clínico poscomercialización",
      "zh-cn": "上市后临床跟踪",
    },
    definition: {
      en: "The proactive collection and evaluation of clinical data from a CE-marked device in normal use, planned to confirm safety and performance, keep the benefit-risk determination current, and detect emerging risks or off-label use.",
      es: "Recogida y evaluación proactivas de datos clínicos de un producto con marcado CE en uso normal, planificadas para confirmar la seguridad y el rendimiento, mantener actualizada la relación beneficio-riesgo y detectar riesgos emergentes o usos fuera de indicación.",
      "zh-cn": "对已加贴 CE 标志器械在常规使用中的临床数据进行主动收集与评价，用以确认安全性与性能、保持获益风险判定的时效性，并发现新出现的风险或超说明书使用。",
    },
  },
  {
    term: "Investigator's Brochure",
    slug: "investigators-brochure",
    category: "clinical",
    synonyms: ["IB"],
    definition: {
      en: "The compilation of clinical and non-clinical data on an investigational product that is relevant to its study in humans. It gives investigators the dosing, safety, and monitoring information they need and is updated as new data emerge.",
      es: "Recopilación de datos clínicos y no clínicos sobre un producto en investigación relevantes para su estudio en humanos. Ofrece a los investigadores la información de dosificación, seguridad y monitorización necesaria y se actualiza a medida que surgen nuevos datos.",
      "zh-cn": "汇总试验用产品中与人体研究相关的临床和非临床数据。它为研究者提供所需的给药、安全性和监测信息，并随新数据的产生而更新。",
    },
  },
  {
    term: "Informed Consent",
    slug: "informed-consent",
    category: "clinical",
    definition: {
      en: "The documented process by which a participant voluntarily confirms willingness to take part in a trial, after being informed of its purpose, procedures, risks, benefits, and their right to withdraw at any time without penalty.",
      es: "Proceso documentado por el que un participante confirma voluntariamente su disposición a participar en un ensayo, tras haber sido informado de su finalidad, procedimientos, riesgos, beneficios y de su derecho a retirarse en cualquier momento sin penalización.",
      "zh-cn": "受试者在获知试验目的、流程、风险、获益以及可随时无条件退出的权利后，自愿确认参加试验的成文过程。",
    },
  },

  // ── Pharmacovigilance ────────────────────────────────────────────────
  {
    term: "GVP",
    slug: "gvp",
    category: "pharmacovigilance",
    expansion: {
      en: "Good Pharmacovigilance Practices",
      es: "Buenas Prácticas de Farmacovigilancia",
      "zh-cn": "药物警戒质量管理规范",
    },
    definition: {
      en: "The EMA modules setting out how marketing authorisation holders and authorities must run pharmacovigilance: quality systems, the pharmacovigilance system master file, risk management, signal management, safety reporting, and post-authorisation safety studies.",
      es: "Módulos de la EMA que establecen cómo deben desarrollar la farmacovigilancia los titulares de autorización y las autoridades: sistemas de calidad, archivo maestro del sistema de farmacovigilancia, gestión de riesgos, gestión de señales, notificación de seguridad y estudios poscomercialización.",
      "zh-cn": "EMA 制定的模块，规定上市许可持有人和监管机构应如何开展药物警戒：质量体系、药物警戒体系主文件、风险管理、信号管理、安全性报告以及上市后安全性研究。",
    },
    relatedHref: "/en/services/pharmacovigilance",
    relatedLabel: {
      en: "Pharmacovigilance services",
      es: "Servicios de farmacovigilancia",
      "zh-cn": "药物警戒服务",
    },
  },
  {
    term: "QPPV",
    slug: "qppv",
    category: "pharmacovigilance",
    expansion: {
      en: "Qualified Person for Pharmacovigilance",
      es: "Persona Cualificada de Farmacovigilancia",
      "zh-cn": "药物警戒质量受权人",
    },
    definition: {
      en: "The individual a marketing authorisation holder must appoint in the EU to establish and maintain the pharmacovigilance system. The QPPV has oversight of the safety profile of all authorised products and is the authorities' round-the-clock point of contact.",
      es: "Persona que un titular de autorización debe designar en la UE para establecer y mantener el sistema de farmacovigilancia. La QPPV supervisa el perfil de seguridad de todos los productos autorizados y es el punto de contacto permanente para las autoridades.",
      "zh-cn": "上市许可持有人必须在欧盟指定的人员，负责建立并维护药物警戒体系。该受权人监督所有获批产品的安全性特征，并作为监管机构全天候的联系人。",
    },
  },
  {
    term: "PSUR",
    slug: "psur",
    category: "pharmacovigilance",
    expansion: {
      en: "Periodic Safety Update Report",
      es: "Informe periódico de seguridad",
      "zh-cn": "定期安全性更新报告",
    },
    definition: {
      en: "The periodic report in which a marketing authorisation holder re-evaluates the benefit-risk balance of an authorised medicine using cumulative safety data, exposure estimates, and signal evaluations over the reporting interval.",
      es: "Informe periódico en el que el titular de la autorización reevalúa el balance beneficio-riesgo de un medicamento autorizado a partir de datos de seguridad acumulados, estimaciones de exposición y evaluaciones de señales durante el intervalo de notificación.",
      "zh-cn": "上市许可持有人依据报告期内累积的安全性数据、暴露量估算和信号评价，重新评估已获批药品获益风险平衡的定期报告。",
    },
  },
  {
    term: "PMS",
    slug: "pms",
    category: "pharmacovigilance",
    expansion: {
      en: "Post-Market Surveillance",
      es: "Vigilancia poscomercialización",
      "zh-cn": "上市后监督",
    },
    definition: {
      en: "The systematic collection and review of experience gained from devices or medicines already on the market, feeding complaint handling, vigilance reporting, risk management, and the periodic safety update report or PSUR equivalent for devices.",
      es: "Recogida y revisión sistemáticas de la experiencia obtenida con productos ya comercializados, que alimenta la gestión de reclamaciones, la notificación de vigilancia, la gestión de riesgos y el informe periódico de seguridad o su equivalente para Medical Devices.",
      "zh-cn": "对已上市器械或药品所获经验的系统收集与审阅，为投诉处理、警戒报告、风险管理以及定期安全性更新报告（器械的对应文件）提供输入。",
    },
  },
  {
    term: "Signal Detection",
    slug: "signal-detection",
    category: "pharmacovigilance",
    definition: {
      en: "The activity of identifying new or changed safety information from spontaneous reports, literature, studies, and other sources, then validating and prioritising it to decide whether the product's risk profile or labelling needs to change.",
      es: "Actividad de identificar información de seguridad nueva o modificada a partir de notificaciones espontáneas, bibliografía, estudios y otras fuentes, y validarla y priorizarla para decidir si debe cambiar el perfil de riesgo o el etiquetado del producto.",
      "zh-cn": "从自发报告、文献、研究及其他来源识别新的或发生变化的安全性信息，并进行验证和优先级排序，以判断产品风险特征或标签是否需要变更。",
    },
  },
  {
    term: "SUSAR",
    slug: "susar",
    category: "pharmacovigilance",
    expansion: {
      en: "Suspected Unexpected Serious Adverse Reaction",
      es: "Sospecha de reacción adversa grave e inesperada",
      "zh-cn": "可疑且非预期严重不良反应",
    },
    definition: {
      en: "A serious adverse reaction in a clinical trial that is suspected to relate to the investigational product and whose nature or severity is not consistent with the reference safety information. SUSARs are subject to expedited reporting to authorities and ethics committees.",
      es: "Reacción adversa grave en un ensayo clínico que se sospecha relacionada con el producto en investigación y cuya naturaleza o gravedad no concuerda con la información de seguridad de referencia. Las SUSAR están sujetas a notificación acelerada a autoridades y comités de ética.",
      "zh-cn": "临床试验中怀疑与试验用产品相关、且其性质或严重程度与参考安全性信息不一致的严重不良反应。SUSAR 须向监管机构和伦理委员会快速报告。",
    },
  },
  {
    term: "Risk Management Plan",
    slug: "risk-management-plan",
    category: "pharmacovigilance",
    synonyms: ["RMP"],
    definition: {
      en: "The document describing what is known and unknown about a medicine's safety, the measures planned to characterise and minimise its risks, and how the effectiveness of those measures will be measured over the product's lifecycle.",
      es: "Documento que describe lo que se conoce y se desconoce sobre la seguridad de un medicamento, las medidas previstas para caracterizar y minimizar sus riesgos y cómo se medirá la eficacia de esas medidas a lo largo del ciclo de vida del producto.",
      "zh-cn": "描述某药品安全性中已知与未知内容、为表征和最小化其风险所计划的措施，以及如何在产品全生命周期内衡量这些措施有效性的文件。",
    },
  },

  // ── Devices & Software ───────────────────────────────────────────────
  {
    term: "SaMD",
    slug: "samd",
    category: "devices",
    popular: true,
    synonyms: ["MDSW", "Medical Device Software", "Software as a Medical Device"],
    expansion: {
      en: "Software as a Medical Device",
      es: "Software como Medical Device",
      "zh-cn": "作为医疗器械的软件",
    },
    definition: {
      en: "Software intended for a medical purpose that performs that purpose on its own, without being part of a hardware device. Under MDR and IVDR it is qualified and classified in its own right, with clinical evidence, usability, and cybersecurity requirements.",
      es: "Software con una finalidad médica que cumple esa finalidad por sí mismo, sin formar parte de un dispositivo físico. Bajo el MDR y el IVDR se califica y clasifica de forma independiente, con requisitos de evidencia clínica, usabilidad y ciberseguridad.",
      "zh-cn": "具有医疗用途并可独立实现该用途、无需依附硬件器械的软件。在 MDR 和 IVDR 下，它单独进行界定和分类，并须满足临床证据、可用性和网络安全要求。",
    },
    relatedHref: "/en/industries/medical-devices/mdsw",
    relatedLabel: {
      en: "Medical Device Software",
      es: "Software para Medical Devices",
      "zh-cn": "医疗器械软件",
    },
  },
  {
    term: "IEC 62304",
    slug: "iec-62304",
    category: "devices",
    definition: {
      en: "The standard defining the software development lifecycle for medical device software: safety classification A to C, planning, requirements, architecture, verification, configuration management, problem resolution, and maintenance after release.",
      es: "Norma que define el ciclo de vida del desarrollo de software para Medical Devices: clasificación de seguridad de A a C, planificación, requisitos, arquitectura, verificación, gestión de la configuración, resolución de problemas y mantenimiento tras el lanzamiento.",
      "zh-cn": "规定医疗器械软件开发生命周期的标准：A 至 C 安全性分级、策划、需求、体系结构、验证、配置管理、问题解决以及发布后的维护。",
    },
  },
  {
    term: "ISO 14971",
    slug: "iso-14971",
    category: "devices",
    definition: {
      en: "The standard for applying risk management to medical devices across the lifecycle: risk analysis, evaluation, control, evaluation of overall residual risk, and production and post-production information feeding back into the risk file.",
      es: "Norma para aplicar la gestión de riesgos a Medical Devices a lo largo del ciclo de vida: análisis, evaluación y control del riesgo, valoración del riesgo residual global e información de producción y posproducción que retroalimenta el expediente de riesgos.",
      "zh-cn": "在医疗器械全生命周期中实施风险管理的标准：风险分析、评价、控制、总体剩余风险评估，以及将生产和生产后信息反馈至风险管理文档。",
    },
  },
  {
    term: "EU AI Act",
    slug: "eu-ai-act",
    category: "devices",
    definition: {
      en: "The European regulation on artificial intelligence, applying a risk-based framework. AI-enabled medical devices and IVDs generally fall in the high-risk category, adding requirements on data governance, transparency, human oversight, robustness, and post-market monitoring on top of MDR or IVDR.",
      es: "Reglamento europeo sobre inteligencia artificial, con un enfoque basado en el riesgo. Los Medical Devices e IVD con IA suelen entrar en la categoría de alto riesgo, lo que añade requisitos de gobernanza de datos, transparencia, supervisión humana, robustez y monitorización poscomercialización sobre el MDR o IVDR.",
      "zh-cn": "欧盟关于人工智能的法规，采用基于风险的框架。含人工智能的医疗器械和 IVD 通常属于高风险类别，在 MDR 或 IVDR 之上还需满足数据治理、透明度、人为监督、稳健性和上市后监测等要求。",
    },
  },
  {
    term: "Cybersecurity",
    slug: "cybersecurity",
    category: "devices",
    definition: {
      en: "For regulated devices, the set of design and process controls protecting confidentiality, integrity, and availability, as described in MDCG 2019-16. It spans secure design, threat modelling, vulnerability handling, and security updates over the supported lifetime.",
      es: "En productos regulados, conjunto de controles de diseño y de proceso que protegen la confidencialidad, la integridad y la disponibilidad, según la guía MDCG 2019-16. Abarca el diseño seguro, el modelado de amenazas, la gestión de vulnerabilidades y las actualizaciones de seguridad durante la vida útil soportada.",
      "zh-cn": "对受监管器械而言，指按照 MDCG 2019-16 指南保护保密性、完整性和可用性的设计与流程控制，涵盖安全设计、威胁建模、漏洞处理以及在支持期内的安全更新。",
    },
  },

  // ── CMC & Manufacturing ──────────────────────────────────────────────
  {
    term: "CSV",
    slug: "csv",
    category: "cmc",
    popular: true,
    expansion: {
      en: "Computer System Validation",
      es: "Validación de sistemas informatizados",
      "zh-cn": "计算机化系统验证",
    },
    definition: {
      en: "Documented evidence that a computerised system used in a GxP process consistently does what it is specified to do. It covers requirements, risk assessment, supplier assessment, testing, data integrity, and controls under EU GMP Annex 11 and 21 CFR Part 11.",
      es: "Evidencia documentada de que un sistema informatizado usado en un proceso GxP hace de forma consistente lo que está especificado. Abarca requisitos, evaluación de riesgos, evaluación del proveedor, pruebas, integridad de datos y controles según el Anexo 11 de las GMP de la UE y 21 CFR Parte 11.",
      "zh-cn": "证明 GxP 流程中使用的计算机化系统能够持续实现其规定功能的成文证据。内容涵盖需求、风险评估、供应商评估、测试、数据完整性，以及欧盟 GMP 附录 11 和 21 CFR Part 11 所要求的控制措施。",
    },
  },
  {
    term: "ICH Q3C",
    slug: "ich-q3c",
    category: "cmc",
    synonyms: ["residual solvents"],
    definition: {
      en: "The ICH guideline on residual solvents in pharmaceuticals. It classifies solvents by toxicity, sets permitted daily exposures and concentration limits, and underpins the analytical control strategy, typically by gas chromatography.",
      es: "Directriz ICH sobre disolventes residuales en medicamentos. Clasifica los disolventes por toxicidad, fija exposiciones diarias permitidas y límites de concentración, y sustenta la estrategia de control analítico, habitualmente por cromatografía de gases.",
      "zh-cn": "关于药品中残留溶剂的 ICH 指南。它按毒性对溶剂分类，规定每日允许暴露量和浓度限度，并为通常采用气相色谱的分析控制策略提供依据。",
    },
  },
  {
    term: "Extractables & Leachables",
    slug: "extractables-and-leachables",
    category: "cmc",
    synonyms: ["E&L", "ICH Q3E"],
    definition: {
      en: "Extractables are compounds that can be drawn out of a container, device, or manufacturing material under exaggerated conditions; leachables are those that actually migrate into the product in normal use. Both are studied to support patient safety assessments.",
      es: "Los extractables son compuestos que pueden extraerse de un envase, dispositivo o material de fabricación en condiciones exageradas; los leachables son los que migran realmente al producto en condiciones normales de uso. Ambos se estudian para respaldar la evaluación de seguridad del paciente.",
      "zh-cn": "可提取物是指在加严条件下可从容器、器械或生产材料中提取出的化合物；浸出物则是在正常使用中实际迁移进入产品的物质。二者均需研究，以支持患者安全性评估。",
    },
  },
  {
    term: "Stability Study",
    slug: "stability-study",
    category: "cmc",
    definition: {
      en: "A study following ICH conditions that measures how a product's quality attributes change over time under defined temperature and humidity, establishing the retest period or shelf life and the recommended storage conditions.",
      es: "Estudio conforme a las condiciones ICH que mide cómo cambian los atributos de calidad de un producto con el tiempo bajo temperatura y humedad definidas, estableciendo el periodo de reanálisis o la vida útil y las condiciones de conservación recomendadas.",
      "zh-cn": "按照 ICH 条件开展的研究，在规定温度和湿度下测定产品质量属性随时间的变化，以确定复验期或有效期以及推荐贮存条件。",
    },
  },
  {
    term: "Batch Release",
    slug: "batch-release",
    category: "cmc",
    definition: {
      en: "The formal decision that a manufactured batch may be supplied. It follows review of the batch record, deviations, and test results, and in the EU requires certification by a Qualified Person against GMP and the marketing authorisation.",
      es: "Decisión formal de que un lote fabricado puede suministrarse. Se toma tras revisar el registro del lote, las desviaciones y los resultados analíticos y, en la UE, requiere la certificación de una Qualified Person frente a las GMP y la autorización de comercialización.",
      "zh-cn": "允许已生产批次供应上市的正式决定。该决定基于对批记录、偏差和检验结果的审核，在欧盟还需由质量受权人依据 GMP 和上市许可进行认证。",
    },
  },
];

export const glossaryText = (value: LocalizedText, lang: SiteLang): string =>
  value[lang] ?? value.en;
